import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/common/PhoneInput';
import type { TeamRegistrationField } from '@/types/tournament/game';
import type { PhoneData } from '@/types/tournament/quickRegistration';
import { useTranslation } from 'react-i18next';

interface DynamicRegistrationFieldProps {
  field: TeamRegistrationField;
  value: string | PhoneData;
  onChange: (value: string | PhoneData) => void;
  onBlur?: () => void;
  error?: string;
  isEmailReadOnly?: boolean;
  defaultEmail?: string;
  isMandatory?: boolean;
}

/**
 * Renders a dynamic registration field based on field configuration
 * Handles all input types: text, email, phone, etc.
 */
export const DynamicRegistrationField = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  isEmailReadOnly = false,
  defaultEmail = '',
  isMandatory = false,
}: DynamicRegistrationFieldProps) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tRegistration } = useTranslation('registration');

  // Prefer existing i18n keys for well-known fields, fall back to backend label
  const translatedLabel =
    field.fieldKey === 'name'
      ? tCommon('form.fullName')
      : field.fieldKey === 'email'
        ? tCommon('form.emailAddress')
        : field.fieldKey === 'inGameName'
          ? tRegistration('quickRegistration.labels.inGameName')
          : field.fieldKey === 'inGameId'
            ? tRegistration('quickRegistration.labels.inGameId')
            : field.fieldLabel;

  // Special handling for phone number field
  if (field.fieldKey === 'mobileNo') {
    const phoneValue =
      typeof value === 'object' && 'number' in value
        ? (value as PhoneData)
        : {
            code: '',
            number: String(value || ''),
            countryName: null,
            abbreviation: null,
          };

    return (
      <div className='flex flex-col'>
        {/* PhoneInput already has its own label and error structure */}
        <PhoneInput
          value={phoneValue}
          onChange={onChange as (phone: PhoneData) => void}
          onBlur={onBlur}
          error={error}
          isRequired={isMandatory}
          countryCodeLabel={tCommon('labels.countryCode')}
          maxLength={field.maxLength}
        />
      </div>
    );
  }

  // Special handling for email field
  if (field.fieldKey === 'email') {
    const emailValue = typeof value === 'string' ? value : defaultEmail || '';
    const displayValue = isEmailReadOnly ? defaultEmail : emailValue;

    return (
      <div className='flex flex-col'>
        {/* Consistent label area - always h-5 to match other fields */}
        <div className='h-5 mb-2'>
          <Label className='text-gray-300'>
            {translatedLabel}
            {isMandatory && <span className='text-red-500'> *</span>}
          </Label>
        </div>
        {/* Input area */}
        <div>
          <Input
            type='email'
            readOnly={isEmailReadOnly}
            className={`text-white ${
              error
                ? 'border-red-500'
                : displayValue.trim()
                  ? 'bg-gray-800/80 border-gray-600'
                  : 'bg-gray-900 border-gray-700'
            } ${isEmailReadOnly ? 'cursor-not-allowed opacity-70' : ''} focus:ring-2 focus:ring-blue-500/30`}
            placeholder={
              tRegistration('playerFields.enterEmail') || 'Enter your email'
            }
            value={displayValue}
            onChange={e => onChange(e.target.value)}
            onBlur={onBlur}
            maxLength={field.maxLength}
          />
        </div>
        {/* Error area - consistent height */}
        <div className='h-5 mt-1'>
          {error && <p className='text-red-400 text-xs'>{error}</p>}
        </div>
      </div>
    );
  }

  // Regular text input fields
  const stringValue = typeof value === 'string' ? value : String(value || '');
  const showCharCounter = field.maxLength !== undefined;

  // Field-specific alignment adjustments
  const alignmentClass = field.fieldKey === 'teamName' ? '-mt-1' : '';

  return (
    <div className={`flex flex-col ${alignmentClass}`}>
      {/* Consistent label area - always h-5 to match other fields */}
      <div className='flex justify-between items-center h-5 mb-2'>
        <Label className='text-gray-300'>
          {translatedLabel}
          {isMandatory && <span className='text-red-500'> *</span>}
        </Label>
        {showCharCounter && (
          <span
            className={`text-xs ${
              stringValue.length > field.maxLength!
                ? 'text-red-400'
                : 'text-gray-400'
            }`}
          >
            {stringValue.length}/{field.maxLength}
          </span>
        )}
      </div>
      {/* Input area */}
      <div>
        <Input
          type={field.inputType === 'EMAIL' ? 'email' : 'text'}
          className={`text-white ${
            error
              ? 'border-red-500'
              : stringValue.trim()
                ? 'bg-gray-800/80 border-gray-600'
                : 'bg-gray-900 border-gray-700'
          } focus:ring-2 focus:ring-blue-500/30`}
          placeholder={
            field.fieldKey === 'name'
              ? translatedLabel
              : field.fieldKey === 'inGameName'
                ? tRegistration('quickRegistration.placeholders.inGameName')
                : field.fieldKey === 'inGameId'
                  ? tRegistration('quickRegistration.placeholders.inGameId')
                  : `Enter ${field.fieldLabel.toLowerCase()}`
          }
          value={stringValue}
          onChange={e => {
            const newValue = e.target.value;
            // Enforce maxLength if specified
            if (field.maxLength !== undefined) {
              onChange(newValue.slice(0, field.maxLength));
            } else {
              onChange(newValue);
            }
          }}
          onBlur={onBlur}
          maxLength={field.maxLength}
        />
      </div>
      {/* Error area - consistent height */}
      <div className='h-5 mt-1'>
        {error && <p className='text-red-400 text-xs'>{error}</p>}
      </div>
    </div>
  );
};
